import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getQuotes, updateQuote, deleteQuote } from '../services/api';
import { toast } from 'sonner';
import { Search, Eye, Trash2, Phone, Mail, MapPin, Calendar } from 'lucide-react';

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchQuotes = async () => {
    try {
      const res = await getQuotes();
      setQuotes(res.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuote(quoteId, { status: newStatus });
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleNotesUpdate = async (quoteId, notes) => {
    try {
      await updateQuote(quoteId, { notes });
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, notes } : q));
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await deleteQuote(quoteId);
      setQuotes(quotes.filter(q => q.id !== quoteId));
      toast.success('Quote deleted');
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.phone?.includes(searchTerm) ||
      quote.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      quoted: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Quote Requests</h1>
        <p className="text-slate-600">Manage incoming quote requests from customers</p>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-white border-2 border-slate-300">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-300"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 bg-white border-slate-300">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Quotes List */}
      <div className="space-y-4">
        {filteredQuotes.length === 0 ? (
          <Card className="p-12 text-center bg-white border-2 border-slate-300">
            <p className="text-slate-500">No quote requests found</p>
          </Card>
        ) : (
          filteredQuotes.map((quote) => (
            <Card key={quote.id} className="p-6 bg-white border-2 border-slate-700">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-slate-900">{quote.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                      {quote.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> {quote.phone}
                    </span>
                    {quote.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {quote.email}
                      </span>
                    )}
                    {quote.address && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {quote.address}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> {new Date(quote.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">Service: {quote.service}</p>
                  {quote.message && (
                    <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">{quote.message}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={quote.status} onValueChange={(value) => handleStatusChange(quote.id, value)}>
                    <SelectTrigger className="w-32 bg-white border-slate-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => { setSelectedQuote(quote); setShowModal(true); }}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(quote.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Quote Details Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Quote Details</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-500">Name</label>
                  <p className="font-medium">{selectedQuote.name}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500">Phone</label>
                  <p className="font-medium">{selectedQuote.phone}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500">Email</label>
                  <p className="font-medium">{selectedQuote.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-slate-500">Service</label>
                  <p className="font-medium">{selectedQuote.service}</p>
                </div>
              </div>
              {selectedQuote.address && (
                <div>
                  <label className="text-sm text-slate-500">Address</label>
                  <p className="font-medium">{selectedQuote.address}</p>
                </div>
              )}
              {selectedQuote.message && (
                <div>
                  <label className="text-sm text-slate-500">Message</label>
                  <p className="font-medium bg-slate-50 p-3 rounded">{selectedQuote.message}</p>
                </div>
              )}
              <div>
                <label className="text-sm text-slate-500 block mb-2">Internal Notes</label>
                <Textarea
                  defaultValue={selectedQuote.notes}
                  placeholder="Add internal notes..."
                  onBlur={(e) => handleNotesUpdate(selectedQuote.id, e.target.value)}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuotes;
